import { Icon, TextButton, UploadZone, useUploadZoneController } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import type { Form } from "../../../../components/form/form";
import { Toggle } from "../../../../components/toggle/toggle";
import type { PaymentPageForm } from "../../../../types/paymentPage";
import type { ModalHeaderForm, ObPaymentPageFormStore } from "../../obPaymentPageFormStore";
import styles from "./companyLogoSection.module.scss";

// The section edits either the page form or the modal form, both carry these fields
type LogoForm = Pick<Form<Pick<PaymentPageForm, "switchedOnLogo" | "logo">>, "values" | "onChange">;

export type CompanyLogoSectionType = {
  form: Form<PaymentPageForm>;
  modalForm: Form<ModalHeaderForm>;
  store: ObPaymentPageFormStore;
  withSeparator?: boolean;
  view?: "modal" | "page";
};

export const CompanyLogoSection: FC<CompanyLogoSectionType> = observer(
  ({ form, modalForm, withSeparator = true, view = "page" }) => {
    const formData: LogoForm = view === "modal" ? modalForm : form;
    const { switchedOnLogo } = formData.values;
    const uploadController = useUploadZoneController();

    const handleToggleChange = (value: boolean): void => {
      formData.onChange("switchedOnLogo", value);
      if (view === "page") {
        modalForm.onChange("switchedOnLogo", value);
      }
      if (!value) {
        formData.onChange("logo", null);
        form.onChange("logoFile", null);
        form.onChange("logoFileData", null);
      }
    };

    const onSelectHandler = async (file: File): Promise<void> => {
      form.onChange("logoFile", file);
      form.onChange("logoFileData", null);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = (): void => {
          // readAsDataURL always produces a string result
          const logo = typeof reader.result === "string" ? reader.result : null;
          form.onChange("logo", logo);
          modalForm.onChange("logo", logo);
        };
        reader.readAsDataURL(file);
      }
    };

    if (form.getValue("logo") && modalForm.getValue("logo")) {
      return null;
    }

    return (
      <div className={classNames(styles.section, withSeparator && styles.withSeparator)}>
        <div className={styles.companyLogoSection}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionSubtitle}>Company logo</span>
              <span className={styles.sectionDescription}>Upload your company logo for a more personalised page</span>
              {switchedOnLogo && <span className={styles.lightText}>Max. file size 100kb. JPEG or PNG files only</span>}
            </div>
            <Toggle
              key="2"
              label="Company logo"
              value={switchedOnLogo}
              onChange={handleToggleChange}
              className={styles.sectionTitle}
            />
          </div>
          {switchedOnLogo && (
            <UploadZone onSelect={onSelectHandler} controller={uploadController} mode="drag-only">
              <TextButton
                size="l"
                className={styles.addButton}
                text="Add logo"
                prefix={<Icon type="actionAdd" />}
                onClick={uploadController.openPicker}
              />
            </UploadZone>
          )}
        </div>
      </div>
    );
  },
);
